import { useCallback, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Fab } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LeftArrow from '@mui/icons-material/ChevronLeft';
import RightArrow from '@mui/icons-material/ChevronRight';
import _, { debounce } from 'lodash';
import { useSetState } from 'react-use';
import { IFilterState, IInheritedMapProps, tEntityID } from '@modules/newMap/WrapperMapView';
import { CEntity, IEntity, CMapLabel, IMapOptions, DATE_RANGE_MODE, CLocation } from '@ts/ui-models';
import ChipEntity from '@components/Outputs/ChipEntity';
import { useRouterContext } from '@utils/context/RouterContext';
import useEffectAfterMount from '@utils/hooks/useEffectAfterMount';
import { CMapLayer } from '@models/maps/Layer';
import { Button, Grid, TextInput, Checkbox, Typography, Icon } from '@ts/storybook';
import clsx from 'clsx';
import WrapperEntitySelector from '@modules/entitySelector/WrapperEntitySelector';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import SectionTimeLine from './SectionTimeline';
import { CMapElement } from '@models/maps/Map';

interface SideBarProps {
  inheritedMapProps: IInheritedMapProps;
  filtersState: IFilterState;
  elementsMapSelected: (CMapElement | CLocation)[];
  onSearchWord: (sectionID: tEntityID, value: string) => void;
  onSearchItem: (value: CEntity<IEntity>[] | null) => void;
  toggleCheckbox: {
    toggleLayer: (id, value) => void;
    toggleLabel: (id, value) => void;
    toggleItem: (id, value) => void;
    toggleAllEntity: (id, value) => void;
  };
}

interface IFilter extends CEntity<IEntity> {
  itemIDs?: string[];
  indeterminate?: boolean;
}

interface ISection {
  id: tEntityID;
  name: string;
  filter: { list: IFilter[]; selected: Set<string> };
  onClickCheckbox: (id: string, value: boolean) => void;
  onClickAccordion: (id: string, value: boolean) => void;
  anyElementInside: (list: any) => boolean;
}

const Sidebar = (props: SideBarProps) => {
  const { filtersState, onSearchItem, elementsMapSelected } = props;
  const { isMobile } = useRouterContext();

  const [accordionState, setAccordionState] = useSetState({
    layers: true,
    labels: false,
    items: false,
  });

  const [itemSelected, setItemSelected] = useState<CEntity<IEntity>[]>([]);
  const [searchWord, setSearchWord] = useState<{ layers: string; labels: string; items: string }>({
    layers: '',
    labels: '',
    items: '',
  });

  const [state, setState] = useSetState<{
    drawerExpanded: boolean;
  }>({
    drawerExpanded: isMobile ? false : true,
  });

  const allSections: Record<tEntityID, ISection> = {
    layers: {
      id: 'layers',
      name: 'Layers',
      filter: filtersState.layers,
      onClickCheckbox: props.toggleCheckbox.toggleLayer,
      onClickAccordion: props.toggleCheckbox.toggleAllEntity,
      anyElementInside: (l: CMapLayer) =>
        Boolean(l.background) ? true : Boolean(l.geometriesIds.length || l.itemIDs.length) ?? true,
    },
    labels: {
      id: 'labels',
      name: 'Labels',
      filter: filtersState.labels,
      onClickCheckbox: props.toggleCheckbox.toggleLabel,
      onClickAccordion: props.toggleCheckbox.toggleAllEntity,
      anyElementInside: (l: CMapLabel) => Boolean(l.itemIDs.length) ?? true,
    },
    items: {
      id: 'items',
      name: 'Items',
      //@ts-ignore
      filter: filtersState.items,
      onClickCheckbox: props.toggleCheckbox.toggleItem,
      onClickAccordion: props.toggleCheckbox.toggleAllEntity,
      anyElementInside: (list: IFilter) => true,
    },
  };

  //#region Handlers
  const toggleDrawer = () => {
    setState({ drawerExpanded: !state.drawerExpanded });
  };

  const onExpandItem = (sectionId: tEntityID) => (event, isExpanded: boolean) => {
    setAccordionState(prevState => ({
      ...prevState,
      [sectionId]: isExpanded,
      // All closed except the new one
      layers: sectionId === 'layers' ? isExpanded : false,
      labels: sectionId === 'labels' ? isExpanded : false,
      items: sectionId === 'items' ? isExpanded : false,
    }));
  };

  const debouncedSearch = useCallback(
    debounce((sectionID, value) => {
      props.onSearchWord(sectionID, value);
    }, 500),
    [filtersState]
  );

  const onChangeSearchWord = (section: ISection, v) => {
    setSearchWord({ ...searchWord, [section.id]: v });
    debouncedSearch(section.id, v);
  };
  const onChangeEntity = (v: CEntity<IEntity> | CEntity<IEntity>[] | null) => {
    onSearchItem(v as CEntity<IEntity>[] | null);
  };
  //#endregion

  //#region Render
  const renderSearchbar = () => {
    return (
      <Grid container size={12} p={1} mb={1}>
        <Grid container size={12} spacing={1} sx={{ justifyContent: 'center' }} className="bg-primary-20/50">
          <Icon icon="search" />
          <Typography variant="h6" text="Search" />
        </Grid>
        <Grid size={12} mt={1} className="px-1">
          <WrapperEntitySelector
            searchComponent={generateSearchEntitySelector('item')}
            label="Items"
            value={itemSelected}
            onChangeEntity={onChangeEntity}
            multiple
            stProps={{ autocomplete: { limitTags: 4 } }}
            //showFilterButton
          />
        </Grid>
      </Grid>
    );
  };

  const renderTimeline = () => {
    return (
      <Grid container size={12} p={1}>
        <Grid container size={12} spacing={1} sx={{ justifyContent: 'center' }} className="bg-primary-20/50">
          <Icon icon="symbols_calendar_clock_outlined" />
          <Typography variant="h6" text="Timeline" />
        </Grid>
        <Grid container size={12} mt={1} className="px-1">
          <SectionTimeLine inheritedMapProps={props.inheritedMapProps} />
        </Grid>
      </Grid>
    );
  };

  const renderFilter = () => {
    return (
      <Grid container size={12} p={1} className="pt-0 flex-1 min-h-0 overflow-y-auto" sx={{ justifyContent: 'flex-end' }}>
        <div className="sticky top-0 z-10 w-full bg-white">
          <Grid container size={12} spacing={1} sx={{ justifyContent: 'center' }} className="bg-primary-20/50">
            <Icon icon="filterAlt" />
            <Typography variant="h6" text="Filters" />
          </Grid>
        </div>
        <Grid size={6} className="mt-4.5 pb-2.5">
          <Button
            label="Reset filters"
            fullWidth
            variant="outlined"
            className="capitalize"
            onClick={() => props.toggleCheckbox.toggleAllEntity('layers', true)}
          />
        </Grid>
        <Grid size={12} className="border-b border-[#808080]">
          {renderAccordion(allSections['layers'])}
        </Grid>
        <Grid size={12} className="border-b border-[#808080]">
          {renderAccordion(allSections['labels'])}
        </Grid>
        <Grid size={12} className="border-b border-[#808080]">
          {renderAccordion(allSections['items'])}
        </Grid>
      </Grid>
    );
  };

  const generateOptionsSection = (listFiltersWithItemsInside: IFilter[], section: ISection) => {
    return listFiltersWithItemsInside
      .filter(e => e.getName().toLowerCase().includes(searchWord[section.id].toLowerCase()))
      .map(entity => {
        const label =
          entity instanceof CMapLabel ? (
            <>
              <ChipEntity
                entity={entity}
                onClick={() => section.onClickCheckbox!(entity.getID(), section.filter.selected.has(entity.getID()))}
              />
              <span style={{ color: 'gray', display: 'block' }}>
                ({entity.itemIDsWithPosition.length}-{entity.itemIDs.length})
              </span>
            </>
          ) : entity instanceof CMapLayer && entity.itemIDs.length > 0 ? (
            <span>
              {entity.getName()}{' '}
              <span style={{ color: 'gray', display: 'block' }}>
                ({entity.itemIDsWithPosition.length}/{entity.itemIDs.length})
              </span>
            </span>
          ) : (
            <span>{entity.getName()}</span>
          );
        return (
          <Checkbox
            key={entity.getID()}
            className="ml-0"
            indeterminate={entity.indeterminate ?? false}
            checked={section.filter.selected.has(entity.getID())}
            onChangeValue={v => section.onClickCheckbox!(entity.getID(), entity.indeterminate ? true : v)}
            label={label}
          />
        );
      });
  };

  const renderAccordion = (section: ISection) => {
    const listFiltersWithItemsInside = section.filter.list && section.filter.list.filter(e => section.anyElementInside(e));
    const selectedOptionsSection = generateOptionsSection(listFiltersWithItemsInside, section);

    return (
      <Accordion
        square
        key={section.id}
        expanded={accordionState[section.id]}
        slotProps={{
          transition: !accordionState[section.id] ? { timeout: 200 } : {},
        }}
        sx={{
          boxShadow:
            '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 0px 0px rgba(0,0,0,0.14), 0px 1px 0px 0px rgba(0,0,0,0.12) !important',
        }}
        onChange={onExpandItem(section.id)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panels-generic" key={section.id}>
          <Checkbox
            indeterminate={section.filter.selected.size !== section.filter.list.length && section.filter.selected.size !== 0}
            className="mr-0"
            //@ts-ignore
            onClick={e => e.stopPropagation()}
            checked={section.filter.selected.size === section.filter.list.length}
            onChangeValue={v => section.onClickAccordion(section.id, v)}
            label={
              <Typography variant="body1" text={[<b>{section.name}</b>, ' ', <b>({listFiltersWithItemsInside.length})</b>]} />
            }
          />
        </AccordionSummary>
        <AccordionDetails key={section.id}>
          <Grid direction="column" container mb={4} className="w-[200px]">
            <TextInput
              label="Search"
              value={searchWord[section.id]}
              onChangeValue={v => onChangeSearchWord(section, v)}
              className="sticky top-0"
              withClear
            />
            <div className="max-h-full">{selectedOptionsSection}</div>
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderBtnToggle = (type: 'open' | 'close') => {
    return (
      <Grid className={clsx('absolute bottom-[calc(50%-52px)] z-[10]', type === 'open' && 'right-[-15px]')}>
        <Fab color="primary" size="small" onClick={toggleDrawer} sx={{ width: 24, height: 24, minHeight: 24 }}>
          {type === 'open' ? <LeftArrow /> : <RightArrow />}
        </Fab>
      </Grid>
    );
  };
  //#endregion

  useEffectAfterMount(() => {
    if (isMobile) {
      setState({ ...state, drawerExpanded: false });
    }
  }, []);

  useEffectAfterMount(() => {
    if (elementsMapSelected.length > 0) {
      setItemSelected(elementsMapSelected);
    } else {
      setItemSelected([]);
    }
  }, [elementsMapSelected]);

  return (
    <div
      className={clsx(
        {
          'max-w-[276px]': state.drawerExpanded,
          ' w-0': !state.drawerExpanded,
        },
        'flex flex-col h-full relative border-r border-t border-t-[#AAAAAA] border-r-[#AAAAAA]'
      )}
    >
      {state.drawerExpanded ? (
        <>
          {renderTimeline()}
          {renderSearchbar()}
          {renderFilter()}
          {!isMobile && renderBtnToggle('open')}
        </>
      ) : (
        !isMobile && renderBtnToggle('close')
      )}
    </div>
  );
};

export default Sidebar;
